import { useGeckoAuth } from 'features/auth'
import { RolodexContext } from 'features/rolodex/RolodexProvider'
import React from 'react'
import useSWRImmutable from 'swr/immutable'
import {
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoMeasurement,
} from 'types'

/**
 * A type encompassing metadata about a particular inspection image, converted
 * back to user-facing coordinates from DS coordinates.
 */
type GeckoInspectionImageMetadata = Readonly<{
  coords: GeckoComponentCoords
  inspection_id: string
  quality: number
  run_id: string
  filename: string
  signed_url: string
}>

type RolodexImageIndex = {
  signed_url: string
  metadata: {
    filename: string
    x: number
    y: number // May not match user-facing coordinates due to zero_offset
    quality: number
    run_id: string
    entry_id: string
  }
}

const chunkSize = 20 // Arbitrary chunk size that "looks good enough"

/**
 * A hook which loads metadata for photos taken by the robot near a given point
 * during an inspection. Note that a CDF is required in order to know both the
 * inspection ID and measurement units involved (bins vs tubes vs degrees, etc.)
 * @param cdf       The component data frame to pull from
 * @param coords    The coordinates to look for images near
 */
export function useRolodexInspectionImages(
  cdf: GeckoComponentDataFrame | undefined,
  coords: GeckoComponentCoords | undefined
) {
  //----------------------------------------------------------------------------

  const { apiURL } = React.useContext(RolodexContext)
  const { authorizedAxios: axios } = useGeckoAuth()

  const { inspectionID, xStart, yStart } = React.useMemo(() => {
    if (!cdf || !cdf.tags.inspection || !coords) return {}

    const inspectionID = cdf.tags.inspection
    // Subtract out the x- and y- offsets applied by Data Systems
    const xOffset = cdf.data.geometry.x.start.minus(cdf.data.binning.offset.x)
    const yOffset = cdf.data.geometry.y.start.minus(cdf.data.binning.offset.y)
    const xBin = coords.x.minus(xOffset).toNumber(cdf.data.geometry.x.unit)
    const yBin = coords.y.minus(yOffset).toNumber(cdf.data.geometry.y.unit)
    const xStart = Math.floor(xBin / chunkSize) * chunkSize
    const yStart = Math.floor(yBin / chunkSize) * chunkSize

    return { inspectionID, xStart, yStart }
  }, [cdf, coords])

  // Fire off queries to fetch image indices for the four closest image "chunks"
  // to the provided coords. Note that we use four separate SWR calls so that
  // we don't have to wait for all four to complete in order to show results
  const [q0, q1, q2, q3] = React.useMemo(() => {
    if (!inspectionID || !apiURL)
      return [undefined, undefined, undefined, undefined]
    if (xStart === undefined || yStart === undefined)
      return [undefined, undefined, undefined, undefined]

    return [-1, 0].flatMap((xOffset) =>
      [-1, 0].flatMap((yOffset) => ({
        url: `${apiURL}/api/v2/entries/${inspectionID}/index/images`,
        body: JSON.stringify({
          x_start: xStart + xOffset * chunkSize,
          x_end: xStart + (xOffset + 1) * chunkSize,
          y_start: yStart + yOffset * chunkSize,
          y_end: yStart + (yOffset + 1) * chunkSize,
          limit: 100,
        }),
      }))
    )
  }, [apiURL, inspectionID, xStart, yStart])

  const fetcher = React.useCallback(
    async (query: {
      url: string
      body: string
    }): Promise<GeckoInspectionImageMetadata[]> =>
      axios
        .post(query.url, query.body, {
          headers: { 'Content-Type': 'application/json' },
        })
        .then((res) => {
          const inspectionID = cdf?.tags.inspection
          if (!res.data || !inspectionID) return []
          const binning = cdf.data.binning
          const xUnit = binning.offset.x.unit
          const yUnit = binning.offset.y.unit
          const componentID = cdf.tags.component
          const xOffset = cdf.data.geometry.x.start.minus(binning.offset.x)
          const yOffset = cdf.data.geometry.y.start.minus(binning.offset.y)
          return (
            res.data as RolodexImageIndex[]
          ).map<GeckoInspectionImageMetadata>(({ metadata, signed_url }) => {
            // Add back in the x- and y- offsets applied by Data Systems
            const imageCoords = GeckoComponentCoords.CreateFromMeasurements(
              componentID,
              GeckoMeasurement.CreateFromValue(metadata.x, xUnit).plus(xOffset),
              GeckoMeasurement.CreateFromValue(metadata.y, yUnit).plus(yOffset)
            )
            return {
              coords: imageCoords,
              inspection_id: inspectionID,
              quality: metadata.quality,
              run_id: metadata.run_id,
              filename: metadata.filename,
              signed_url: signed_url,
            }
          })
        }),
    [axios, cdf]
  )

  //----------------------------------------------------------------------------

  const { data: d0, isLoading: l0, error: e0 } = useSWRImmutable(q0, fetcher)
  const { data: d1, isLoading: l1, error: e1 } = useSWRImmutable(q1, fetcher)
  const { data: d2, isLoading: l2, error: e2 } = useSWRImmutable(q2, fetcher)
  const { data: d3, isLoading: l3, error: e3 } = useSWRImmutable(q3, fetcher)

  const data = React.useMemo<ReadonlyArray<GeckoInspectionImageMetadata>>(
    () => [d3 ?? [], d2 ?? [], d1 ?? [], d0 ?? []].flat(),
    [d0, d1, d2, d3]
  )

  return {
    data,
    isLoading: l3 ?? l2 ?? l1 ?? l0,
    error: e3 ?? e2 ?? e1 ?? e0,
  }
}
